import { useEffect, useState } from 'react';
import { fetchReportScreenshot } from '../utils/api';

/**
 * Object URLs keyed by ScreenshotRef.key. A key absent from this record is
 * still loading; a key present with value `null` failed to fetch (a terminal
 * state, distinct from "loading forever").
 */
export type ScreenshotSources = Record<string, string | null>;

/**
 * Proof-of-scan images for one report (epic #1331), fetched through authFetch
 * because an <img src> cannot carry the Clerk Bearer token. Each blob becomes
 * an object URL that is revoked when the inputs change or the component
 * unmounts. A key whose fetch throws is recorded as `null` so the caller can
 * render a terminal "unavailable" state instead of an indefinite placeholder.
 */
const EMPTY: ScreenshotSources = {};

export function useReportScreenshots(
  reportId: string | null | undefined,
  keys: string[],
  shareToken?: string | null,
): ScreenshotSources {
  // Keys are compared by value, not by array identity, so a caller can pass a
  // freshly mapped array every render without re-fetching.
  const keyList = keys.join('\n');
  // Sources are tagged with the inputs that fetched them. Keys repeat across
  // reports ('0-home' is on every one), so images held over from the previous
  // report while the next one loads would show one site's screenshot under
  // another site's report, even for a single render. `request` must encode
  // every input the fetch below reads; an input left out of it would slip past
  // the stale-image guard in the return.
  const request = `${reportId ?? ''}\n${shareToken ?? ''}\n${keyList}`;
  const [loaded, setLoaded] = useState<{ request: string; sources: ScreenshotSources } | null>(null);

  useEffect(() => {
    if (!reportId || keyList === '') {
      setLoaded(null);
      return undefined;
    }

    let cancelled = false;
    const created: string[] = [];
    const wanted = keyList.split('\n');

    void Promise.all(
      wanted.map(async (key): Promise<[string, string | null] | null> => {
        try {
          const blob = await fetchReportScreenshot(reportId, key, shareToken);
          if (cancelled) return null;
          const src = URL.createObjectURL(blob);
          created.push(src);
          return [key, src];
        } catch {
          return [key, null];
        }
      }),
    ).then((entries) => {
      if (cancelled) return;
      setLoaded({
        request,
        sources: Object.fromEntries(entries.filter((e): e is [string, string | null] => e !== null)),
      });
    });

    return () => {
      cancelled = true;
      for (const src of created) URL.revokeObjectURL(src);
    };
  }, [reportId, keyList, shareToken, request]);

  return loaded?.request === request ? loaded.sources : EMPTY;
}
